import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Key, Shield, Loader2, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin user management: view all users, toggle promoter role, reset password.
 */
const UsersPanel = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [savingReset, setSavingReset] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const togglePromoter = async (u) => {
    try {
      await axios.post(
        `${API}/admin/promoter-role`,
        { user_id: u.id, is_promoter: !u.is_promoter },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${u.name} ${!u.is_promoter ? 'is now a promoter' : 'is no longer a promoter'}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update role');
    }
  };

  const toggleInternal = async (u) => {
    try {
      await axios.post(
        `${API}/admin/internal-role`,
        { user_id: u.id, is_internal: !u.is_internal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `${u.name} ${!u.is_internal ? 'can now access the Internal guest list' : 'no longer has Internal access'}`
      );
      fetchUsers();
    } catch {
      toast.error('Failed to update Internal access');
    }
  };

  const openResetPassword = (u) => {
    setResetUser(u);
    setNewPassword('');
    setResetOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingReset(true);
    try {
      await axios.post(
        `${API}/admin/password-reset`,
        { user_id: resetUser.id, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset for ${resetUser.name}`);
      setResetOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSavingReset(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 size={32} className="animate-spin text-[#B5FF00] mx-auto" />
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="users-table">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">NAME</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">EMAIL</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">ROLES</th>
              <th className="text-right py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                className="border-b border-zinc-800/50 hover:bg-zinc-900/50"
                data-testid={`user-row-${u.id}`}
              >
                <td className="py-4 px-4 text-white">{u.name}</td>
                <td className="py-4 px-4 text-zinc-400 font-mono text-sm">{u.email}</td>
                <td className="py-4 px-4">
                  <div className="flex gap-2">
                    {u.is_admin && (
                      <span className="font-mono text-xs px-2 py-1 bg-red-500/10 text-red-400 border border-red-500/30">
                        ADMIN
                      </span>
                    )}
                    {u.is_promoter && (
                      <span className="font-mono text-xs px-2 py-1 bg-[#B5FF00]/10 text-[#B5FF00] border border-[#B5FF00]/30">
                        PROMOTER
                      </span>
                    )}
                    {u.is_internal && (
                      <span className="font-mono text-xs px-2 py-1 bg-blue-500/10 text-blue-300 border border-blue-500/40" data-testid={`badge-internal-${u.id}`}>
                        INTERNAL
                      </span>
                    )}
                    {!u.is_admin && !u.is_promoter && !u.is_internal && (
                      <span className="font-mono text-xs px-2 py-1 bg-zinc-800 text-zinc-400">USER</span>
                    )}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-end gap-2">
                    {!u.is_admin && (
                      <button
                        onClick={() => togglePromoter(u)}
                        className="p-2 text-zinc-400 hover:text-[#B5FF00] transition-colors"
                        title={u.is_promoter ? 'Revoke promoter' : 'Grant promoter'}
                        data-testid={`toggle-promoter-${u.id}`}
                      >
                        <Shield size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => toggleInternal(u)}
                      className={`p-2 transition-colors ${
                        u.is_internal ? 'text-blue-300' : 'text-zinc-400 hover:text-blue-300'
                      }`}
                      title={u.is_internal ? 'Revoke Internal-list access' : 'Grant Internal-list access'}
                      data-testid={`toggle-internal-${u.id}`}
                    >
                      <KeyRound size={18} />
                    </button>
                    <button
                      onClick={() => openResetPassword(u)}
                      className="p-2 text-zinc-400 hover:text-yellow-400 transition-colors"
                      title="Reset password"
                      data-testid={`reset-password-${u.id}`}
                    >
                      <Key size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Josefin_Sans'] text-2xl">RESET PASSWORD</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <p className="text-zinc-400 text-sm">
              Setting a new password for <span className="text-white">{resetUser?.name}</span> ({resetUser?.email})
            </p>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6 chars)"
              className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
              autoFocus
              data-testid="new-password-input"
            />
            <div className="flex gap-4">
              <button type="button" onClick={() => setResetOpen(false)} className="btn-secondary flex-1">
                CANCEL
              </button>
              <button
                type="submit"
                disabled={savingReset}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="confirm-reset-password-btn"
              >
                {savingReset ? <><Loader2 size={18} className="animate-spin" />...</> : 'RESET'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPanel;
